import BackIcon from "@assets/icons/back.svg";
import InfoIcon from "@assets/icons/info.svg";
import { useI18n } from "@core/i18n/i18n";
import { useGoBack } from "@core/routing/use-navigate";
import styled from "@emotion/styled";
import { TertiaryButton } from "@ui/components/buttons/tertiary-button";
import { TextInput } from "@ui/components/inputs/text-input";
import { IconPosition, MenuButton, MenuItemProps } from "@ui/components/common/menu-button";
import { Tooltip } from "@ui/components/common/tooltip";
import { useDimensions } from "@ui/hooks/use-dimensions";
import { physioMeMobileBreakpoint, row, stack } from "@ui/style";
import { colors } from "@ui/style/colors";
import { isNotNil } from "@utils/assert";
import React, { useMemo, useState } from "react";

interface PageHeadProps {
	title: string;
	tooltip?: string;
	canGoBack?: boolean;
	displaySearch?: boolean;
	primaryButton?: JSX.Element;
	tertiaryButton?: { label: string; onClick: () => void; icon?: JSX.Element | string };
	menuItems?: MenuItemProps[];
	className?: string;
	style?: React.CSSProperties;
}

export const PageHeader: React.FC<PageHeadProps> = ({
	title,
	tooltip,
	canGoBack,
	displaySearch,
	primaryButton,
	tertiaryButton,
	menuItems,
	className,
	style,
}) => {
	const [search, setSearch] = useState("");
	const goBack = useGoBack();
	const { format } = useI18n();
	const { isMobile } = useDimensions();

	const displayRightPanel = useMemo(
		() => displaySearch || isNotNil(primaryButton) || isNotNil(tertiaryButton),
		[displaySearch, primaryButton, tertiaryButton],
	);

	return (
		<Header className={className} style={style}>
			<Row>
				<div>
					{canGoBack && (
						<BackContainer onClick={goBack}>
							<BackIcon color={colors.primary} />
							<BackTitle className="p-size-s-bold">{format("global.dialog.back")}</BackTitle>
						</BackContainer>
					)}
					<Row>
						<p className={isMobile ? "p-header-3" : "p-header-2"}>{title}</p>
						{isNotNil(tooltip) && (
							<Tooltip content={tooltip}>
								<IconContainer>
									<StyledInfoIcon width={20} height={20} color={colors.grey80} />
								</IconContainer>
							</Tooltip>
						)}
					</Row>
				</div>
				{isNotNil(menuItems) && (
					<ActionButton iconPosition={IconPosition.Left} itemIconColor={colors.primary} menuItems={menuItems} />
				)}
			</Row>
			{displayRightPanel && (
				<RightPanel>
					{displaySearch && (
						<span
							className="p-input-icon-left p-input-icon-right"
							style={{ flex: 1, backgroundColor: colors.primaryBackground }}
						>
							<i className="pi pi-search" />
							<TextInput
								style={{ width: "100%" }}
								placeholder={format("global.search.placeholder")}
								value={search}
								onChange={e => setSearch(e.target.value)}
							/>
							<ClearSearchButton className="pi pi-times" displayed={!!search} onClick={() => setSearch("")} />
						</span>
					)}
					<ButtonsContainer>
						{isNotNil(tertiaryButton) && (
							<TertiaryButton
								label={tertiaryButton.label}
								onClick={tertiaryButton.onClick}
								icon={tertiaryButton.icon}
							/>
						)}
						{isNotNil(primaryButton) && primaryButton}
					</ButtonsContainer>
				</RightPanel>
			)}
		</Header>
	);
};

const Header = styled.div`
	${row(24, "spacebetween", "center")};
	padding: 40px;

	@media ${physioMeMobileBreakpoint} {
		${stack(24)}
		padding: 24px;
	}
`;

const Row = styled.div`
	${row(undefined, "spacebetween", "center")};
	align-self: stretch;
`;

const BackContainer = styled.div`
	${row(8, undefined, "center")};
	cursor: pointer;
	margin-bottom: 4px;
`;
const BackTitle = styled.p`
	color: ${colors.primary};
`;

const ClearSearchButton = styled.i<{ displayed: boolean }>`
	cursor: pointer;
	display: ${({ displayed }) => (displayed ? undefined : "none")};

	:hover {
		color: ${colors.primary} !important;
	}
`;

const RightPanel = styled.div`
	${row(24, "center", "center")};

	@media ${physioMeMobileBreakpoint} {
		${stack(24)}
	}
`;

const ActionButton = styled(MenuButton)`
	.menu-button {
		background-color: ${colors.transparent};

		:hover {
			background-color: ${colors.primary10};
		}
	}
`;

const StyledInfoIcon = styled(InfoIcon)`
	align-self: center;
	justify-self: center;
`;

const IconContainer = styled.div`
	display: flex;
	align-items: center;
	margin-left: 8px;
`;

const ButtonsContainer = styled.div`
	${row(24, undefined, "center")};
	justify-content: center;
	@media ${physioMeMobileBreakpoint} {
		flex-direction: row-reverse;
		display: flex;
		gap: 16px;
	}
`;
